import { useState, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Select,
  Text,
  Heading,
  useOutsideClick,
  Collapse,
  Divider,
  Stack,
  useDisclosure,
  List,
  ListItem,
  InputRightElement,
  InputGroup,
  InputLeftAddon,
  ModalFooter,
  ModalContent,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  Tooltip
} from "@chakra-ui/react"
import { FaDownload, FaFilter, FaSearch } from "react-icons/fa"
import { IoIosArrowDown } from "react-icons/io"
import { LocalDate } from "@js-joda/core"
import { ArrowDownIcon2 } from "@/styles/icons/ArrowDownIcon2"
import { CalendarIcon, DownloadIcon } from "@/styles/icons"
import { DollarIcon } from "@/styles/icons/DollarIcon"
import { StatusIcon } from "@/styles/icons/StatusIcon"
import { MethodIcon } from "@/styles/icons/MethodIcon"
import { useCharge } from "@/hooks/useCharge"
import { format } from "date-fns"
import { formatDate, showToast } from "@/utils"
import { useAsyncFunction } from "@/hooks/useAsyncFunction"
type FilterOption = {
  icon: React.ReactNode
  id: string
  label: string
  options?: { label: string; value: string }[]
  type?: "date" | "select" | "checkbox" | "range" | "text"
}

interface FilterState {
  status: string | null
  customerName: string | null
  customerDocument: string | null
  customerEmail: string | null
  chargeTitle: string | null
  expirationDateInitial: LocalDate | null
  expirationDateFinal: LocalDate | null
  paidDateInitial: LocalDate | null
  paidDateFinal: LocalDate | null
  paymentLink: string | null
  valueMin: number | null
  valueMax: number | null
  paymentMethod: string | null
}

interface ChargeTableFilterProps {
  onFilterChange: (filterState: FilterState) => void
  currentFilters?: FilterState
}

export function ChargeTableFilter({ onFilterChange, currentFilters }: ChargeTableFilterProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<string[]>([])
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)
  const downloadRef = useRef<HTMLDivElement>(null)
  const inputEmailRef = useRef<HTMLInputElement>(null)
  const { downloadCSV } = useCharge();
  const { isOpen: isSearchExpanded, onToggle: toggleSearch } = useDisclosure();
  const { isOpen: isDropdownOpen, onToggle: toggleDropdown } = useDisclosure();
  const { isOpen: isDownloadOpen, onOpen: openDownload, onClose: closeDownload } = useDisclosure();
  const [searchType, setSearchType] = useState("customerName");
  const [searchTerm, setSearchTerm] = useState("");
  const user = JSON.parse(localStorage.getItem("barte-user-info") || "{}");
  const { execute, loading } = useAsyncFunction(downloadCSV);
  const clearFilterState = {
    status: "ALL",
    customerName: null,
    customerDocument: null,
    customerEmail: null,
    chargeTitle: null,
    expirationDateInitial: null,
    expirationDateFinal: null,
    paidDateInitial: null,
    paidDateFinal: null,
    paymentLink: null,
    valueMin: null,
    valueMax: null,
    paymentMethod: null
  }
  const searchTypeOptions = [
    { label: "Cliente", value: "customerName" },
    { label: "CPF/CNPJ", value: "customerDocument" },
    { label: "Email", value: "customerEmail" },
    { label: "Título da Cobrança", value: "chargeTitle" },
    { label: "ID - Link de pagamento", value: "paymentLink" }
  ];

  const formatSearchType = (type: string) => {
    const searchType = {
      customerName: "Cliente",
      customerDocument: "CPF/CNPJ",
      customerEmail: "Email",
      chargeTitle: "Título da Cobrança",
      paymentLink: "ID - Link de pagamento"
    }
    return searchType[type as keyof typeof searchType] || type;
  }

  const searchRef = useRef<HTMLDivElement>(null);

  const [filterState, setFilterState] = useState<FilterState>({
      status: currentFilters?.status || "ALL",
      customerName: currentFilters?.customerName || null,
      customerDocument: currentFilters?.customerDocument || null,
      customerEmail: currentFilters?.customerEmail || null,
      chargeTitle: currentFilters?.chargeTitle || null,
      expirationDateInitial: currentFilters?.expirationDateInitial || null,
      expirationDateFinal: currentFilters?.expirationDateFinal || null,
      paidDateInitial: currentFilters?.paidDateInitial || null,
      paidDateFinal: currentFilters?.paidDateFinal || null,
      paymentLink: currentFilters?.paymentLink || null,
      valueMin: currentFilters?.valueMin || null,
      valueMax: currentFilters?.valueMax || null,
      paymentMethod: currentFilters?.paymentMethod || null
  });

  const selectSearchType = (type: string) => {
    setSearchType(type);
    toggleDropdown();
  };

  const filterOptions: FilterOption[] = [
    { 
      icon: <CalendarIcon size={12} />,
      id: "data-pagamento", 
      label: "Data de pagamento", 
      type: "date" 
    },
    { 
      icon: <CalendarIcon size={12} />,
      id: "data-vencimento", 
      label: "Data de vencimento", 
      type: "date" 
    },
    { 
      icon: <DollarIcon size={12} />,
      id: "valor", 
      label: "Valor", 
      type: "range" 
    },
    {
      icon: <StatusIcon size={12} />,
      id: "status",
      label: "Status",
      options: [
        { label: "Todas", value: "ALL" },
        { label: "Pagas", value: "PAID" },
        { label: "Agendadas", value: "SCHEDULED" },
        { label: "Atrasadas", value: "LATE" },
        { label: "Canceladas", value: "CANCELED" },
        { label: "Desistência", value: "ABANDONED" },
        { label: "Estornadas", value: "CHARGEBACK" },
        { label: "Erro", value: "FAILED" },
      ]
    },
    {
      icon: <MethodIcon size={12} />,
      id: "paymentMethod",
      label: "Método de pagamento",
      options: [
        { label: "Cartão", value: "CREDIT_CARD" },
        { label: "Boleto", value: "BANK_SLIP" },
        { label: "Pix", value: "PIX" }
      ]
    },
  ]

  useOutsideClick({
    ref: filterRef,
    handler: () => setIsFilterOpen(false),
  })

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen)
  }

  const toggleFilterOption = (id: string) => {
    if (activeFilters.includes(id)) {
      setActiveFilters(activeFilters.filter((filterId) => filterId !== id))
    } else {
      setActiveFilters([...activeFilters, id])
    }
  }

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterState(clearFilterState);
    localStorage.removeItem("chargeTableFilters");
    setHasAppliedFilters(false)
    setIsFilterOpen(false)
    onFilterChange(clearFilterState);
  };

  const handleFilterChange = (id: string, value: any) => {
    setFilterState(prev => ({
      ...prev,
      [id]: value
    }))
  }

  const applyFilters = () => {
    const hasSearchTerm = searchTerm.trim() !== ""
    const hasFilterChanges = JSON.stringify(filterState) !== JSON.stringify(clearFilterState)
    setHasAppliedFilters(hasSearchTerm || hasFilterChanges)
    setIsFilterOpen(false)
    onFilterChange(filterState)
  }

  const handleClickButton = (e: any) => {
    e.preventDefault();
    if(searchTerm === "") {
      applyFilters();
      toggleSearch();
    } else {
      applyFilters();
    }
  }

  const handleConfirmDownload = async () => {
    try{
      const formatData = {
        expirationDateInitial: filterState?.expirationDateInitial ? filterState?.expirationDateInitial.toString() : "",
        expirationDateFinal: filterState?.expirationDateFinal ? filterState?.expirationDateFinal.toString() : "",
        recipient: inputEmailRef.current?.value
      }
      await execute(formatData);
      showToast({
        type: "success",
        message: "Relatório gerado com sucesso. Será enviado para o email informado.",
      });
    } catch {
      showToast({
        type: "error",
        message: "Falha ao gerar relatório.",
      });
    } finally {
      closeDownload();
    }
  };

  return (
    <>
      <Modal isOpen={isDownloadOpen} onClose={closeDownload} isCentered>
        <ModalOverlay />
        <ModalContent maxW="400px">
          <ModalHeader>Baixar relatório .csv</ModalHeader>
          <ModalBody
            as="form"
            display="flex"
            flexDir="column"
            gap="2rem"
          >
            <Text>
              O relatório será gerado com os filtros que estão aplicados na visualização atual e ficará disponível para download na Central de notificações.
            </Text>
            <Input
              type="email" 
              placeholder="seu-email@dominio.com" 
              name="email"
              ref={inputEmailRef}
              defaultValue={user?.email}
              _focus={{
                borderColor: "pink.500"
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button 
              onClick={handleConfirmDownload}
              backgroundColor="pink.500"
              color="white"
              isLoading={loading}
            >
              Ok
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Flex 
        w="100%" 
      >
        <Flex
          w="100%"
          flexDir="column"
          gap="2"
        >
          <Flex
            align="center"  
            gap="2"
          >
            <Box ref={searchRef}>
              <Flex 
                borderWidth="1px" 
                borderRadius="md" 
                overflow="hidden"
                borderColor={(isInputFocused || searchTerm !== "") ? "pink.500" : "gray.300"}
                as="form"
                onSubmit={(e) => handleClickButton(e)}
              >
                <InputGroup>
                  <InputLeftAddon 
                    px={3}
                    py={2}
                    bg="white"
                    backgroundColor="#FCFAFB"
                    color={(isInputFocused || searchTerm !== "") ? "pink.500" : "gray.300"}
                    cursor="pointer"
                    onClick={toggleDropdown}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap="2"
                    border="none"
                    h="32px"
                  >
                    <Text
                      fontSize="14px"
                    >
                      {formatSearchType(searchType)}
                    </Text>
                    <IoIosArrowDown size="12px"/>
                  </InputLeftAddon>
                  
                  <Input
                    id="search-input"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      handleFilterChange(searchType, e.target.value === "" ? null : e.target.value)
                    }}
                    px={3}
                    py={2}
                    h="32px"
                    border="none"
                    outline="none"
                    _focus={{ boxShadow: "none" }}
                    placeholder="Buscar..."
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                  />
                  
                  <InputRightElement
                    w="32px"
                    h="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p="2"
                  >
                    <Button 
                      variant="ghost2" 
                      color="gray.400"
                      type="submit"
                      border="none"
                      _focus={{ boxShadow: "none", outline: "none" }}
                      _focusVisible={{ boxShadow: "none", outline: "none" }}
                      p={0}
                    >
                      <FaSearch size="16px" color="currentColor"/>
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </Flex>
              {isDropdownOpen && (
                <Box
                  position="absolute"
                  zIndex="dropdown"
                  mt={1}
                  w="full"
                  bg="white"
                  borderWidth="1px"
                  borderRadius="md"
                  boxShadow="lg"
                  maxW="120px"
                >
                  <List>
                    {searchTypeOptions.map((item) => (
                      <ListItem
                        key={item.value}
                        px={4}
                        py={2}
                        _hover={{ bg: "gray.100" }}
                        cursor="pointer"
                        onClick={() => selectSearchType(item.value)}
                      >
                        {item.label}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
            <Box ref={filterRef}>
              <Button
                onClick={toggleFilter}
                variant="ghost2"
                colorScheme={hasAppliedFilters ? "pink" : "gray"}
                color={hasAppliedFilters ? "pink.500" : "gray.700"}
                borderColor={hasAppliedFilters ? "pink.500" : "gray.300"}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="2"
                h="32px"
                _hover={{
                  backgroundColor: "#000",
                  color: "#fff",
                  borderColor: "#000"
                }}
              >
                <FaFilter size="14px" color="currentColor"/>
                <Text
                  fontSize="16px"
                  fontWeight="400"
                >
                  Filtro
                </Text>
              </Button>

              <AnimatePresence>
                {isFilterOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 0 }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      position: "relative",
                    }}
                  >
                    <Box
                      position="absolute"
                      right="0"
                      mt="2"
                      w="72"
                      bg="white"
                      borderWidth="1px"
                      borderRadius="md"
                      boxShadow="lg"
                      zIndex="20"
                      overflow="hidden"
                    >
                      <Flex justify="space-between" align="center" p="4" borderBottomWidth="1px">
                        <Heading as="h3" size="sm" fontWeight="medium">
                          Filtros
                        </Heading>
                        <Button variant="link" color="pink.500" fontSize="sm" onClick={handleClearFilters}>
                          Limpar Todos
                        </Button>
                      </Flex>

                      <Box p="4" maxH="96" overflowY="auto">
                        {filterOptions.map((option) => (
                          <Box key={option.id} mb="4">
                            <Flex
                              align="center"
                              justify="space-between"
                              cursor="pointer"
                              mb="2"
                              onClick={() => toggleFilterOption(option.id)}
                            >
                              <Flex align="center">
                                <Text mr="2">
                                  {option.icon}
                                </Text>
                                <Text>{option.label}</Text>
                              </Flex>
                              <Box
                                transition="transform 0.2s"
                                transform={
                                  activeFilters.includes(option.id) 
                                    ? "rotate(180deg)" 
                                    : "rotate(0deg)"
                                }
                              >
                                <ArrowDownIcon2 />
                              </Box>
                            </Flex>

                            <Collapse in={activeFilters.includes(option.id)}>
                              {option.type === "date" && (
                                <Flex pl="6" mt="2" gap="2">
                                  <Input 
                                    type="date"
                                    size="sm"
                                    value={option.id === "data-pagamento" 
                                      ? filterState.paidDateInitial?.toString() 
                                      : filterState.expirationDateInitial?.toString()}
                                    onChange={(e) => {
                                      const value = e.target.value || null
                                      if (option.id === "data-pagamento") {
                                        handleFilterChange("paidDateInitial", value)
                                      } else {
                                        handleFilterChange("expirationDateInitial", value)
                                      }
                                    }}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                  <Input 
                                    type="date"
                                    size="sm"
                                    value={option.id === "data-pagamento" 
                                      ? filterState.paidDateFinal?.toString() 
                                      : filterState.expirationDateFinal?.toString()}
                                    onChange={(e) => {
                                      const value = e.target.value || null
                                      if (option.id === "data-pagamento") {
                                        handleFilterChange("paidDateFinal", value)
                                      } else {
                                        handleFilterChange("expirationDateFinal", value)
                                      }
                                    }}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                </Flex>
                              )}

                              {option.type === "range" && (
                                <Flex pl="6" mt="2" gap="2">
                                  <Input 
                                    type="number"
                                    placeholder="Min"
                                    size="sm"
                                    value={filterState.valueMin || ""}
                                    onChange={(e) => handleFilterChange("valueMin", parseFloat(e.target.value.replaceAll(",", ".")))}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                  <Input 
                                    type="number"
                                    placeholder="Max"
                                    size="sm"
                                    value={filterState.valueMax || ""}
                                    onChange={(e) => handleFilterChange("valueMax", parseFloat(e.target.value.replaceAll(",", ".")))}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                </Flex>
                              )}

                              {option.type === "text" && (
                                <Box pl="6" mt="2">
                                  <Input
                                    size="sm"
                                    value={
                                      option.id === "cliente" ? filterState.customerName || "" :
                                      option.id === "documento" ? filterState.customerDocument || "" :
                                      option.id === "email" ? filterState.customerEmail || "" :
                                      option.id === "titulo" ? filterState.chargeTitle || "" :
                                      option.id === "link" ? filterState.paymentLink || "" :
                                      ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value || null
                                      switch(option.id) {
                                        case "cliente":
                                          handleFilterChange("customerName", value)
                                          break
                                        case "documento":
                                          handleFilterChange("customerDocument", value)
                                          break
                                        case "email":
                                          handleFilterChange("customerEmail", value)
                                          break
                                        case "titulo":
                                          handleFilterChange("chargeTitle", value)
                                          break
                                        case "link":
                                          handleFilterChange("paymentLink", value)
                                          break
                                      }
                                    }}
                                  />
                                </Box>
                              )}

                              {!option.type && option.options && (
                                <Box pl="6" mt="2">
                                  <Select 
                                    size="sm"
                                    value={filterState[`${option.id}`] || ""}
                                    onChange={(e) => handleFilterChange(option.id, e.target.value)}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  >
                                    {option.options.map((opt) => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </Select>
                                </Box>
                              )}
                            </Collapse>
                          </Box>
                        ))}
                      </Box>

                      <Divider />
                      <Flex p="4" justify="flex-end">
                        <Button 
                          colorScheme="pink" 
                          size="sm"
                          onClick={applyFilters}
                        >
                          Aplicar Filtros
                        </Button>
                      </Flex>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
            <Box ref={downloadRef}>
              <Button
                variant="ghost2"
                onClick={openDownload}
                _hover={{
                  backgroundColor: "#000",
                  color: "#fff",
                  borderColor: "#000"
                }}
                fontSize="14px"
                fontWeight="400"
                borderColor="gray.300"
                size="sm"
                isDisabled={filterState?.expirationDateInitial === null || filterState?.expirationDateFinal === null}
              >
                <Tooltip visibility={filterState?.expirationDateInitial === null || filterState?.expirationDateFinal === null ? "visible" : "hidden"} label="Selecione uma data de vencimento para poder gerar o relatório de cobranças no formato CSV.">
                  <Flex align="center" gap="2">
                    <DownloadIcon size={16} color="currentColor" />
                    <Text>
                      Csv
                    </Text>
                  </Flex>
                </Tooltip>
            </Button>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </>

  )
}